import parse from 'html-react-parser';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import TruncateMarkup from 'react-truncate-markup';

import { CategoryListItemExt, PostListItem as Post, PostSubTypes, ReactionStats } from 'api';
import { PostTopBar } from 'components/PostTopBar';
import { ReactionsWrapper } from 'components/ReactionsWrapper';
import { Tag } from 'features/documentList/components/DocumentMainInfo/styled';
import { useModal, useWindowSize } from 'hooks';
import { theme } from 'styles';
import { InViewItemRef } from 'types';

import { PostAttachments } from '../PostAttachments';
import { ReactionConfirmDialog } from '../ReactionConfirmDialog';
import { BirthdayPost } from './BirthdayPost';
import { KpiTargetPost } from './KpiTargetPost';
import { PollOptions } from './PollOptions';
import { PollPost } from './PollPost';
import {
  LeftSide,
  PostDescription,
  PostTitle,
  PostW,
  Container,
  TagW,
  PostContentWrapper,
  TagContainer,
} from './styled';
import { usePostItem } from './usePostListItem';
import { WelcomePost } from './WelcomePost';

type PostListItemProps = Pick<
  Post,
  | 'id'
  | 'title'
  | 'description'
  | 'attachments'
  | 'author'
  | 'comments_count'
  | 'type'
  | 'meta'
  | 'poll'
> & {
  lastItemRef?: InViewItemRef;
  isPreview?: boolean;
  published_at?: string;
  tags?: string[];
  reactions_stats?: ReactionStats;
  categoriesList: CategoryListItemExt[];
  isSlider?: boolean;
  pin: boolean;
  hideAttachments?: boolean;
};

export function PostListItem({
  attachments,
  lastItemRef,
  comments_count,
  isPreview = false,
  author,
  id,
  pin,
  meta,
  poll,
  reactions_stats,
  categoriesList,
  published_at = '',
  isSlider,
  hideAttachments = false,
  ...otherProps
}: PostListItemProps): ReactElement {
  const {
    authorName,
    postTitle,
    categoryTags = [],
    postRef,
    textWithNewLines,
    isWelcomePost,
    isBirthdayPost,
    isKPICompletedPost,
    birthDate,
    isPollPost,
    handlePostClick,
    goToPostDetails,
    onNavigateToNewUser,
  } = usePostItem({
    author,
    isPreview,
    id,
    meta,
    categoriesList,
    poll,
    ...otherProps,
  });
  const isPoll = !!poll;
  const { t } = useTranslation();
  const { handleOpenModal, handleCloseModal, isOpen } = useModal();
  const { isMacAir } = useWindowSize();

  if (isWelcomePost) {
    return (
      <>
        <WelcomePost
          id={id}
          author={author}
          authorName={authorName}
          publishedAt={published_at}
          reactions={reactions_stats}
          comments_count={comments_count}
          postRef={postRef}
          lastItemRef={lastItemRef}
          onNavigateToNewUser={onNavigateToNewUser}
          handlePostClick={handlePostClick}
          handleOpenReactionModal={handleOpenModal}
          isSlider={isSlider}
        />
        {isOpen && (
          <ReactionConfirmDialog
            postId={id}
            open={isOpen}
            title={t('reactions.title')}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }

  if (isBirthdayPost) {
    return (
      <>
        <BirthdayPost
          id={id}
          author={author}
          authorName={authorName}
          birthDate={birthDate}
          publishedAt={published_at}
          reactions={reactions_stats}
          comments_count={comments_count}
          postRef={postRef}
          lastItemRef={lastItemRef}
          onNavigateToNewUser={onNavigateToNewUser}
          handlePostClick={handlePostClick}
          handleOpenReactionModal={handleOpenModal}
          isSlider={isSlider}
        />
        {isOpen && (
          <ReactionConfirmDialog
            postId={id}
            open={isOpen}
            title={t('reactions.title')}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }

  if (isPollPost && meta?.sub_type === PostSubTypes.POLL) {
    return (
      <>
        <PollPost
          id={id}
          author={author}
          authorName={authorName}
          publishedAt={published_at}
          reactions={reactions_stats}
          comments_count={comments_count}
          postRef={postRef}
          handlePostClick={handlePostClick}
          handleOpenReactionModal={handleOpenModal}
          lastItemRef={lastItemRef}
          isSlider={isSlider}
        />
        {isOpen && (
          <ReactionConfirmDialog
            postId={id}
            open={isOpen}
            title={t('reactions.title')}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }

  if (isKPICompletedPost && meta?.kpi_target_id) {
    return (
      <>
        <KpiTargetPost
          id={id}
          author={author}
          postRef={postRef}
          publishedAt={published_at}
          lastItemRef={lastItemRef}
          handlePostClick={handlePostClick}
          handleOpenReactionModal={handleOpenModal}
          targetId={meta?.kpi_target_id}
          comments_count={comments_count}
          reactions={reactions_stats}
          isSlider={isSlider}
        />
        {isOpen && (
          <ReactionConfirmDialog
            postId={id}
            open={isOpen}
            title={t('reactions.title')}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }

  return (
    <Container ref={postRef}>
      <PostW ref={lastItemRef} isSlider={isSlider} hideAttachments={hideAttachments}>
        <PostContentWrapper>
          <PostTopBar
            id={id}
            isPoll={isPoll}
            postTitle={postTitle}
            authorName={authorName}
            isAutomatedPost={isPoll}
            author={author}
            publishedAt={published_at}
            pin={pin}
          />
          <LeftSide>
            <TagContainer>
              {categoryTags?.length ? (
                <>
                  {categoryTags.slice(0, 2).map((categoryTag) => (
                    <TagW key={categoryTag.id}>
                      <Tag
                        sx={{
                          backgroundColor: theme.palette.info.light,
                          color: theme.palette.primary.main,
                          height: '36px',
                          width: isMacAir ? '160px' : '220px',
                          margin: 0,
                        }}
                      >
                        {categoryTag.title}
                      </Tag>
                    </TagW>
                  ))}
                  {categoryTags.length > 2 && (
                    <TagW>
                      <Tag sx={{ margin: 0, color: 'inherit', textDecoration: 'underline' }}>
                        {t('common.moreTags', { count: categoryTags.length - 2 })}
                      </Tag>
                    </TagW>
                  )}
                </>
              ) : null}
            </TagContainer>

            <PostTitle>{postTitle}</PostTitle>
            <TruncateMarkup
              lines={
                isPoll && !!attachments?.length
                  ? 2
                  : Boolean(attachments && attachments.length > 0)
                  ? 3
                  : 13
              }
              ellipsis={<span>...</span>}
            >
              <PostDescription
                hasAttachments={Boolean(attachments && attachments.length > 0)}
                isPoll={isPoll}
              >
                {parse(textWithNewLines)}
              </PostDescription>
            </TruncateMarkup>

            {attachments && !hideAttachments && (
              <PostAttachments
                postId={id}
                isPreview={isPreview}
                attachments={attachments}
                goToPostDetails={goToPostDetails}
              />
            )}

            {poll && <PollOptions {...poll} postId={id} goToPollDetails={goToPostDetails} />}
          </LeftSide>
        </PostContentWrapper>

        <ReactionsWrapper
          id={id}
          isPreview={isPreview}
          hideAttachments={hideAttachments}
          reactions={reactions_stats}
          comments_count={comments_count}
          attachments_count={attachments?.length}
          mediaCount={attachments?.filter((att) => att.type !== 'document').length}
          docCount={attachments?.filter((att) => att.type === 'document').length}
          handlePostClick={handlePostClick}
          handleOpenReactionModal={handleOpenModal}
        />
      </PostW>
      {isOpen && (
        <ReactionConfirmDialog
          postId={id}
          open={isOpen}
          title={t('reactions.title')}
          onClose={handleCloseModal}
        />
      )}
    </Container>
  );
}
